import React, { useEffect, useRef, useState } from 'react';
// import { useNavigate } from 'react-router';
import { EmeraldDropdown } from '@emerald-react/dropdown';
import { EmeraldButton } from '@emerald-react/button';
import { EmeraldList, EmeraldListItem } from '@emerald-react/list';
import cx from 'classnames';
import {
  angularVersions,
  emitter,
  frameworksSupported,
  reactVersions,
  styles,
  versions,
} from './../constants';

export const FrameworkSelection = (props: any) => {
  // const navigate = useNavigate();
  const codegenHistory = useRef<any>({});
  const getVersionOptions = (framework?: string) => {
    const frameworkSelected = framework || props.frameworkSelected;
    return (
      frameworkSelected?.toLowerCase() === 'react'
        ? reactVersions
        : frameworkSelected?.toLowerCase() === 'angular'
        ? angularVersions
        : []
    ).map((each: any) => ({
      ...each,
      selected: each.value === props.frameworkVersionSelected,
    }));
  };
  // structuredClone(
  //   versions[props.frameworkSelected as keyof typeof versions],
  // ) || [];
  const [frameworks, setFrameworks] = useState<any[]>(
    structuredClone(frameworksSupported),
  );
  const [versionOptions, setVersionOptions] = useState<any[]>(
    getVersionOptions(),
  );
  const [styleOptions, setStyleOptions] = useState<any[]>(
    structuredClone(styles),
  );
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [minimisedFrameworkDetails, setMinimisedFrameworkDetails] =
    useState<boolean>(false);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [codeGenerationInProcess, setCodeGenerationInProcess] =
    useState<boolean>(false);

  const newValues = (options: any[], selectedValue: string) => {
    const newOptions = structuredClone(options);
    newOptions.forEach(
      (option: any) => (option.selected = option.value === selectedValue),
    );
    return newOptions;
  };

  const codegenResultHandler = (data: any) => {
    if (data?.key && data?.chatResult) {
      codegenHistory.current[data.key] = data;
      emitter.emit('updateHistory', {
        history: codegenHistory.current,
        key: data.key,
      });
      historySectionChangeHandler(data);
    }
  };

  const historyFoundHandler = (data: any) => {
    codegenHistory.current = data;
    const options: any[] = Object.keys(codegenHistory.current).map(key => ({
      label: key,
      value: key,
    }));
    setHistoryList(options);
  };

  const historySectionChangeHandler = (data: any) => {
    if (data?.key) {
      const options: any[] = Object.keys(codegenHistory.current).map(key => ({
        label: key,
        value: key,
      }));
      setHistoryList(newValues(options, data.key));
      const [framework, version, styleType] = data.key.split('-');
      //setFrameworks(newValues(frameworksSupported, framework));
      props.setFrameworkSelected(framework);
      //setVersionOptions(newValues(getVersionOptions(framework), version));
      props.setFrameworkVersionSelected(version);
      //setStyleOptions(newValues(styles, styleType));
      props.setFrameworkStyleSelected(styleType);
      emitter.emit('historySelected', codegenHistory.current[data.key] || {});
    }
  };

  const oldCodegenDataUpdateHandler = (data: any) => {
    if (data?.oldData) {
      setChatHistory(
        data.oldData.map((item: any, index: number) => ({
          label: getPromptTitle(item?.codeSection),
          title: getPromptTitle(item?.codeSection),
          selected: index === data.selectedIndex,
        })),
      );
    }
  };

  const getPromptTitle = (codeSection: any) => {
    if (codeSection.isEmeraldPrompt) {
      return 'Emerald Code Generation';
    }
    if (!codeSection || !codeSection.userPrompt) {
      return 'Default Prompt';
    }
    return codeSection.userPrompt;
  };

  const codeGenerationInProcessHandler = (data: any) =>
    setCodeGenerationInProcess(!!data);

  useEffect(() => {
    emitter.on('codegenResult', codegenResultHandler.bind(this));
    emitter.on('historyFound', historyFoundHandler.bind(this));
    emitter.on('historySectionChange', historySectionChangeHandler.bind(this));
    emitter.on('oldCodegenDataUpdate', oldCodegenDataUpdateHandler.bind(this));
    emitter.on(
      'codeGenerationInProcess',
      codeGenerationInProcessHandler.bind(this),
    );
    return () => {
      emitter.off('codegenResult', codegenResultHandler);
      emitter.off('historyFound', historyFoundHandler);
      emitter.off('historySectionChange', historySectionChangeHandler);
      emitter.off('oldCodegenDataUpdate', oldCodegenDataUpdateHandler);
      emitter.off('codeGenerationInProcess', codeGenerationInProcessHandler);
    };
  }, []);

  useEffect(() => {
    setFrameworks(newValues(frameworks, props.frameworkSelected));
    setVersionOptions(
      newValues(
        getVersionOptions(props.frameworkSelected?.toLowerCase()),
        props.frameworkVersionSelected,
      ),
    );
    setStyleOptions(newValues(styleOptions, props.frameworkStyleSelected));
  }, [
    props.frameworkSelected,
    props.frameworkVersionSelected,
    props.frameworkStyleSelected,
  ]);

  const isGenerateCodeDisabled = () => {
    if (
      !props.frameworkSelected ||
      !props.frameworkVersionSelected ||
      !props.frameworkStyleSelected
    ) {
      return true;
    }
    return isHistoryAvailable();
  };

  const isHistoryAvailable = () => {
    const key = `${props.frameworkSelected}-${props.frameworkVersionSelected}-${props.frameworkStyleSelected}`;
    return !!codegenHistory.current[key];
  };

  return (
    <div
      className={cx('framework-details-select', {
        minimised: minimisedFrameworkDetails,
      })}
    >
      <EmeraldDropdown
        label="Select a history"
        size="small"
        onChange={(event: any, value: any) => {
          setHistoryList(newValues(historyList, value[0]));
          emitter.emit('historySectionChange', { key: value[0] });
        }}
        options={historyList}
        editable={false}
        disabled={!historyList?.length || codeGenerationInProcess}
        className={cx({
          'framework-details-invisible': minimisedFrameworkDetails,
        })}
      />
      <label
        className={cx('sub-heading', {
          'framework-details-invisible': minimisedFrameworkDetails,
        })}
      >
        Generate Code
      </label>
      <EmeraldDropdown
        label="Framework"
        size="small"
        onChange={(event: any, value: any) => {
          setFrameworks(newValues(frameworksSupported, value[0]));
          props.setFrameworkSelected(value[0]);
          setVersionOptions(
            structuredClone(getVersionOptions(value[0]?.toLowerCase())),
          );
          props.setFrameworkVersionSelected('');
          const key = `${value[0]}-${props.frameworkVersionSelected}-${props.frameworkStyleSelected}`;
          emitter.emit('historySelected', codegenHistory.current[key] || {});
        }}
        options={frameworks}
        editable={false}
        className={cx({
          'framework-details-invisible': minimisedFrameworkDetails,
        })}
        disabled={codeGenerationInProcess}
      />
      <EmeraldDropdown
        label="Version"
        size="small"
        onChange={(event: any, value: any) => {
          setVersionOptions(newValues(getVersionOptions(), value[0]));
          props.setFrameworkVersionSelected(value[0]);
          const key = `${props.frameworkSelected}-${value[0]}-${props.frameworkStyleSelected}`;
          emitter.emit('historySelected', codegenHistory.current[key] || {});
        }}
        options={versionOptions}
        editable={false}
        className={cx({
          'framework-details-invisible': minimisedFrameworkDetails,
        })}
        disabled={codeGenerationInProcess}
      />
      <EmeraldDropdown
        label="Style"
        size="small"
        onChange={(event: any, value: any) => {
          setStyleOptions(newValues(styles, value[0]));
          props.setFrameworkStyleSelected(value[0]);
          const key = `${props.frameworkSelected}-${props.frameworkVersionSelected}-${value[0]}`;
          emitter.emit('historySelected', codegenHistory.current[key] || {});
        }}
        options={styleOptions}
        editable={false}
        className={cx({
          'framework-details-invisible': minimisedFrameworkDetails,
        })}
        disabled={codeGenerationInProcess}
      />
      {
        <style
          dangerouslySetInnerHTML={{
            __html: `.attached-to-body.opened { z-index: 1000; }`,
          }}
        />
      }
      <div
        className={cx('user-history', {
          'framework-details-invisible': minimisedFrameworkDetails,
        })}
      >
        <span className="sub-heading">Chat History</span>
        <EmeraldList>
          {chatHistory.map((item: any, index: number) => (
            <EmeraldListItem
              key={index}
              label={item.label}
              title={item.title}
              selected={item.selected}
              disabled={codeGenerationInProcess}
              onClick={() => {
                setChatHistory(
                  chatHistory.map((chatItem, chatIndex) => ({
                    ...chatItem,
                    selected: chatIndex === index,
                  })),
                );
                emitter.emit('selectedOldDataIndex', {
                  index,
                  userPrompt: item.label,
                });
              }}
            />
          ))}
          {chatHistory.length === 0 && <span>No chat history available</span>}
        </EmeraldList>
      </div>
      <EmeraldButton
        label="Generate Code"
        onClick={() => {
          const key = `${props.frameworkSelected}-${props.frameworkVersionSelected}-${props.frameworkStyleSelected}`;
          codegenHistory.current[key] = {};
          emitter.emit('triggerAI', {
            frameworkSelected: props.frameworkSelected,
            frameworkVersionSelected: props.frameworkVersionSelected,
            frameworkStyleSelected: props.frameworkStyleSelected,
          });
        }}
        disabled={isGenerateCodeDisabled() || codeGenerationInProcess}
        className={cx('generate-code-button', {
          'framework-details-invisible': minimisedFrameworkDetails,
        })}
      />
      <EmeraldButton
        label="Emerald Code"
        onClick={() => {
          const key = `${props.frameworkSelected}-${props.frameworkVersionSelected}-${props.frameworkStyleSelected}`;
          codegenHistory.current[key] = {};
          emitter.emit('emeraldPrompt', {
            frameworkSelected: props.frameworkSelected,
          });
        }}
        disabled={!isHistoryAvailable() || codeGenerationInProcess}
        className={cx('generate-code-button', {
          'framework-details-invisible': minimisedFrameworkDetails,
        })}
      />
      <i
        className="emerald-icon material-symbols-outlined expand-collapse"
        aria-hidden="false"
        onClick={() => setMinimisedFrameworkDetails(!minimisedFrameworkDetails)}
      >
        {minimisedFrameworkDetails
          ? 'keyboard_double_arrow_right'
          : 'keyboard_double_arrow_left'}
      </i>
    </div>
  );
};
